import { Component, computed, effect, inject, input, Type, ChangeDetectionStrategy } from '@angular/core';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItemDivider,
  IonItemGroup,
  IonList,
  IonRow,
  IonSpinner,
  IonText,
  ModalController,
  ToastController,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { IonicScrollManagerService } from '@okcargo-apps/layout-ionic';
import { MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import { APP_CONFIG } from '@okcargo-apps/shared-infrastructure';
import {
  DecaStopType,
  IDecaDocument,
  IDecaDocumentSegment,
  isDecaChangesBlockedForCarrier,
  UUID,
} from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { ClipboardService } from '@okcargo-apps/shared-infrastructure-ionic';
import { areIntervalsOverlapping, endOfDay, isBefore, startOfDay } from 'date-fns';
import { DateFnsModule } from 'ngx-date-fns';
import { AccountService } from '../../account';
import { DecaAddReservationModalComponent } from '../deca-add-reservation-modal/deca-add-reservation-modal.component';
import { DecaDetailSegmentComponent } from '../deca-detail-segment/deca-detail-segment.component';
import { DecaEditAddressModalComponent } from '../deca-edit-address-modal/deca-edit-address-modal.component';
import { DecaEditAuthorizationModalComponent } from '../deca-edit-authorization-modal/deca-edit-authorization-modal.component';
import { DecaEditDateModalComponent } from '../deca-edit-date-modal/deca-edit-date-modal.component';
import { DecaEditGoodsDescriptionModalComponent } from '../deca-edit-goods-description-modal/deca-edit-goods-description-modal.component';
import { DecaEditNetWeightModalComponent } from '../deca-edit-net-weight-modal/deca-edit-net-weight-modal.component';
import { DecaEditPlatesModalComponent } from '../deca-edit-plates-modal/deca-edit-plates-modal.component';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-detail',
  templateUrl: './deca-detail.component.html',
  styleUrls: ['./deca-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonText,
    IonItemDivider,
    IonItemGroup,
    IonList,
    IonIcon,
    IonButton,
    IonSpinner,
    IonGrid,
    IonRow,
    IonCol,
    DateFnsModule,
    TranslateModule,
    MessageUiIonicComponent,
    DecaDetailSegmentComponent,
  ],
})
export class DecaDetailComponent {
  public readonly id = input.required<UUID>();

  private readonly modalCtrl = inject(ModalController);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly accountService = inject(AccountService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly toastCtrl = inject(ToastController);
  private readonly config = inject(APP_CONFIG);

  public messages = computed(() => this.messagesUiService.getMessagesForObjectId(this.id()));

  protected readonly currentDocumentVM = this.decaStoreService.currentDocumentVM;

  protected readonly document = computed(() => this.currentDocumentVM().item);
  protected readonly isLoading = computed(() => this.currentDocumentVM().isLoading);
  protected readonly hasError = computed(() => this.currentDocumentVM().error);

  protected readonly userCompanyId = computed(() => this.accountService.account().company.id);

  /** True when an action that needs the backend cannot be performed (no connectivity). */
  protected readonly actionsDisabled = this.decaStoreService.actionsDisabled;

  protected readonly isCarrier = computed(() => this.document()?.carrier.id === this.userCompanyId());
  protected readonly isShipper = computed(() => this.document()?.shipper.id === this.userCompanyId());
  protected readonly isCreator = computed(() => this.document()?.createdByCompanyId === this.userCompanyId());

  protected readonly isEditBlockedByShipper = computed(() => {
    const doc = this.document();
    const companyId = this.userCompanyId();
    return !!doc && !!companyId && isDecaChangesBlockedForCarrier(doc, companyId);
  });

  // The changes of a change-blocked document are managed in the assigned freights of the carriers
  // web app, whose URL is the same in every environment but not the host of every app (e.g. docs).
  protected readonly changesBlockedFreightsUrl = `${this.config.carriersUrl}/tabs/freight-inprogress`;

  protected readonly canModifyGoodsData = computed(
    () => this.accountService.hasCapability(ECapabilities.MODIFY_DECA_GOODS_DATA) && !this.isEditBlockedByShipper(),
  );
  protected readonly canModifyLocations = computed(
    () => this.accountService.hasCapability(ECapabilities.MODIFY_DECA_LOCATIONS) && !this.isEditBlockedByShipper(),
  );
  protected readonly canModifyDates = computed(
    () => this.accountService.hasCapability(ECapabilities.MODIFY_DECA_DATES) && !this.isEditBlockedByShipper(),
  );
  protected readonly canModifyVehicles = computed(
    () => this.accountService.hasCapability(ECapabilities.MODIFY_DECA_VEHICLES) && !this.isEditBlockedByShipper(),
  );
  protected readonly canModifyReservations = computed(
    () => this.accountService.hasCapability(ECapabilities.MODIFY_DECA_RESERVATIONS) && !this.isEditBlockedByShipper(),
  );

  protected readonly hasMissingNetWeight = computed(
    () => this.document()?.segments?.some((seg) => !seg.netWeight) ?? false,
  );
  protected readonly hasMissingPlates = computed(() => !this.document()?.tractorPlateNumber);

  // TODO make these dates reactive; revisit alongside the backoffice dashboard solution
  private readonly todayStart = startOfDay(new Date());
  private readonly todayEnd = endOfDay(new Date());
  private readonly todayInterval = { start: this.todayStart, end: this.todayEnd };

  protected isUpcomingDocument = computed(() => {
    const doc = this.document();
    const originDate = doc?.segments?.[0]?.origin.date;
    const destinationDate = doc?.segments?.[doc.segments.length - 1]?.destination.date;

    if (originDate && destinationDate) {
      const start = new Date(originDate);
      const end = new Date(destinationDate);
      const docInterval = { start, end };

      const overlapsToday = areIntervalsOverlapping(docInterval, this.todayInterval);
      const isFuture = start > this.todayEnd;

      return overlapsToday || isFuture;
    }

    return false;
  });

  protected isPastDocument = computed(() => {
    const doc = this.document();
    const originDate = doc?.segments?.[0]?.origin.date;
    const destinationDate = doc?.segments?.[doc.segments.length - 1]?.destination.date;

    if (!originDate || !destinationDate) {
      return false;
    }

    return isBefore(new Date(destinationDate), this.todayStart);
  });

  constructor() {
    effect(() => {
      const inputId = this.id();
      const currentId = this.document()?.id;
      if (inputId !== currentId) {
        this.decaStoreService.setSelectedDocumentId(inputId);
      }
    });
  }

  private async openDocumentModal<T>(component: Type<T>, extraProps: Record<string, unknown> = {}): Promise<void> {
    if (this.actionsDisabled()) {
      return;
    }

    const document = this.document();
    if (!document) return;

    const userCompanyId = this.userCompanyId();

    const modal = await this.modalCtrl.create({
      component,
      componentProps: {
        document,
        userCompanyId,
        ...extraProps,
      },
    });

    await modal.present();

    const { role, data } = await modal.onWillDismiss<IDecaDocument>();

    if (role === 'confirm' && data) {
      this.ionicScrollManagerService.scrollToTop();
    }
  }

  public openEditPlatesModal(): Promise<void> {
    return this.openDocumentModal(DecaEditPlatesModalComponent);
  }

  public openAddReservationModal(): Promise<void> {
    return this.openDocumentModal(DecaAddReservationModalComponent);
  }

  public openEditDateModal(segment: IDecaDocumentSegment, stopType: DecaStopType): Promise<void> {
    return this.openDocumentModal(DecaEditDateModalComponent, { segment, stopType });
  }

  public openEditAuthorizationModal(): Promise<void> {
    return this.openDocumentModal(DecaEditAuthorizationModalComponent);
  }

  public openEditGoodsDescriptionModal(segment: IDecaDocumentSegment): Promise<void> {
    return this.openDocumentModal(DecaEditGoodsDescriptionModalComponent, { segment });
  }

  public openEditNetWeightModal(segment: IDecaDocumentSegment): Promise<void> {
    return this.openDocumentModal(DecaEditNetWeightModalComponent, { segment });
  }

  public openEditAddressModal(segment: IDecaDocumentSegment, stopType: DecaStopType): Promise<void> {
    return this.openDocumentModal(DecaEditAddressModalComponent, { segment, stopType });
  }

  protected async copyPdfUrl(): Promise<void> {
    const document = this.document();
    if (!document?.pdfUrl) return;

    const isCopied = await this.clipboardService.writeText(document.pdfUrl);

    const toast = await this.toastCtrl.create({
      message: isCopied ? 'Enlace copiado' : 'No se ha podido copiar el enlace',
      duration: 3000,
      color: isCopied ? 'success' : 'danger',
      position: 'bottom',
    });

    await toast.present();
  }
}
